import { useState } from 'react';

export default function Module2() {
  const [activeSection, setActiveSection] = useState<number | null>(null);

  const sections = [
    {
      id: 1,
      title: "什么是一般贸易？",
      subtitle: "定义与本质",
      icon: "ri-building-2-line",
      content: {
        definition: "我国境内有进出口经营权的企业单边进口或单边出口的贸易方式",
        essence: "单笔申报、逐票核销、依法退税",
        details: [
          {
            title: "法律定义",
            content: "根据《海关法》和相关法规，一般贸易是指中国境内有进出口经营权的企业，在法定口岸，按一般贸易监管方式进出口的货物。"
          },
          {
            title: "监管特点",
            content: "海关对每一票货物进行独立监管，实行\"一票一报\"制度，每票货物都需要单独申报、查验、放行。"
          },
          {
            title: "核心要素",
            content: "必须具备进出口经营权、在指定口岸操作、按规定缴纳税费、享受相应的出口退税政策。"
          }
        ]
      }
    },
    {
      id: 2,
      title: "核心优势与劣势",
      subtitle: "全面对比分析",
      icon: "ri-scales-line",
      content: {
        advantages: [
          {
            title: "出口退税",
            description: "利润的重要组成部分",
            example: "产品征13%增值税，退10%，相当于成本降低10%",
            impact: "对企业盈利能力影响巨大",
            calculation: "假设100万货值，可退税10万元"
          },
          {
            title: "适用范围广",
            description: "几乎所有产品和企业都适用",
            example: "从电子产品到服装纺织，从机械设备到化工产品",
            impact: "为企业提供最大的业务灵活性",
            calculation: "覆盖95%以上的出口商品"
          },
          {
            title: "提升企业形象",
            description: "操作规范，利于与大型客户合作",
            example: "跨国公司更倾向于选择合规的供应商",
            impact: "提高客户信任度和订单获取率",
            calculation: "合规企业获得大单概率提升30%"
          },
          {
            title: "品牌建设",
            description: "有利于自主品牌的培育和发展",
            example: "可以进行品牌注册、商标保护等",
            impact: "为企业长远发展奠定基础",
            calculation: "品牌溢价可达20-50%"
          }
        ],
        disadvantages: [
          {
            title: "单证齐全要求高",
            description: "必须提供增值税发票等合法凭证才能退税",
            challenge: "采购环节必须规范，无票货物无法操作",
            cost: "合规成本增加5-10%",
            solution: "建立完善的供应商管理体系"
          },
          {
            title: "流程复杂",
            description: "涉及多个政府部门的协调配合",
            challenge: "国税局（退税）、外汇管理局（收汇）、海关（报关）",
            cost: "人员培训和管理成本较高",
            solution: "配备专业的外贸和财务团队"
          },
          {
            title: "税务合规成本高",
            description: "需要专业的财务人员处理复杂税务事项",
            challenge: "退税申报、汇率核算、税务稽查应对",
            cost: "财务人员成本占总成本2-3%",
            solution: "建立标准化的财务管理流程"
          },
          {
            title: "资金周转压力",
            description: "退税周期较长，占用企业流动资金",
            challenge: "退税到账需要3-12个月",
            cost: "资金成本增加年化6-8%",
            solution: "合理安排资金计划和融资渠道"
          }
        ]
      }
    },
    {
      id: 3,
      title: "业务流程与关键节点",
      subtitle: "操作实务详解",
      icon: "ri-flow-chart",
      content: {
        flowSteps: [
          {
            step: "接订单",
            description: "与客户签订出口合同",
            keyPoints: ["合同条款审核", "价格确认", "交期安排"],
            documents: ["出口合同", "形式发票"],
            department: "业务部门",
            duration: "1-3天"
          },
          {
            step: "采购备货",
            description: "采购原材料并取得进项发票",
            keyPoints: ["供应商选择", "发票合规性", "成本控制"],
            documents: ["采购合同", "增值税专用发票", "入库单"],
            department: "采购部门",
            duration: "7-30天"
          },
          {
            step: "生产制造",
            description: "按订单要求组织生产",
            keyPoints: ["质量控制", "交期管理", "成本核算"],
            documents: ["生产计划", "质检报告", "出库单"],
            department: "生产部门", 
            duration: "15-45天"
          },
          {
            step: "出口报关",
            description: "向海关申报并办理出口手续",
            keyPoints: ["单证齐全", "商品归类", "价格申报"],
            documents: ["报关单", "发票", "装箱单", "合同"],
            department: "报关部门",
            duration: "1-3天"
          },
          {
            step: "货物出运",
            description: "安排运输并取得运输单据",
            keyPoints: ["运输安排", "保险购买", "单据交接"],
            documents: ["提单", "保险单", "装运通知"],
            department: "物流部门",
            duration: "1-30天"
          },
          {
            step: "收汇核销",
            description: "收取外汇并办理核销手续",
            keyPoints: ["汇率确认", "到账核实", "外汇核销"],
            documents: ["银行水单", "核销单", "收汇通知"],
            department: "财务部门",
            duration: "30-90天"
          },
          {
            step: "退税申报",
            description: "向税务部门申请出口退税",
            keyPoints: ["单证齐全", "信息齐备", "及时申报"],
            documents: ["退税申报表", "出口发票", "报关单", "收汇凭证"],
            department: "财务部门",
            duration: "90-365天"
          }
        ],
        taxRefundDetails: {
          conditions: [
            "报关出口 - 货物必须实际离境",
            "财务做销售处理 - 会计账务规范",
            "收汇到账 - 外汇收入合规",
            "进项发票认证 - 取得合法发票"
          ],
          timeline: "通常在出口后3-12个月内完成",
          rates: [
            { category: "机电产品", rate: "13%" },
            { category: "纺织服装", rate: "11%" },
            { category: "化工产品", rate: "9%" },
            { category: "农产品", rate: "9%" }
          ]
        },
        roleResponsibilities: [
          {
            role: "业务员",
            responsibilities: [
              "客户沟通和订单获取",
              "合同条款谈判和确认",
              "交期跟踪和客户维护",
              "市场信息收集和分析"
            ]
          },
          {
            role: "跟单员", 
            responsibilities: [
              "生产进度跟踪和协调",
              "质量监控和问题处理",
              "物流安排和单据制作",
              "各部门沟通协调"
            ]
          },
          {
            role: "财务人员",
            responsibilities: [
              "成本核算和价格分析",
              "税务处理和退税申报",
              "外汇管理和汇率核算",
              "资金计划和风险控制"
            ]
          }
        ]
      }
    },
    {
      id: 4,
      title: "适用场景分析",
      subtitle: "选择标准与建议",
      icon: "ri-target-line",
      content: {
        scenarios: [
          {
            type: "生产型企业",
            description: "拥有自主生产能力的制造企业",
            characteristics: [
              "有自己的工厂和生产线",
              "能够控制产品质量和交期",
              "具备规模化生产能力",
              "原材料采购渠道稳定"
            ],
            advantages: [
              "可享受完整的出口退税政策",
              "利润空间相对较大",
              "客户信任度较高",
              "便于品牌建设"
            ],
            examples: "电子制造、服装生产、机械加工、化工生产等企业"
          },
          {
            type: "工贸一体企业",
            description: "集生产和贸易功能于一体的综合型企业",
            characteristics: [
              "既有生产能力又有贸易资质",
              "可灵活调配内外部资源",
              "业务模式多样化",
              "风险分散能力强"
            ],
            advantages: [
              "业务灵活性强",
              "可承接多样化订单",
              "抗风险能力较强",
              "发展空间广阔"
            ],
            examples: "大型制造集团、产业链整合企业、品牌运营商等"
          },
          {
            type: "高价值产品业务",
            description: "单票货值较高的出口业务",
            characteristics: [
              "产品单价高、利润丰厚",
              "品类相对单一",
              "订单量大、批次稳定",
              "客户要求规范化操作"
            ],
            advantages: [
              "退税金额可观",
              "操作成本相对较低",
              "便于精细化管理",
              "客户粘性较强"
            ],
            examples: "精密机械、高端电子、医疗器械、新能源设备等"
          },
          {
            type: "品牌导向企业",
            description: "注重自主品牌建设的出口企业",
            characteristics: [
              "拥有自主品牌和知识产权",
              "注重品质和服务",
              "市场定位明确",
              "长期发展规划清晰"
            ],
            advantages: [
              "有利于品牌保护",
              "提升产品附加值",
              "建立客户忠诚度",
              "获得政策支持"
            ],
            examples: "消费电子品牌、服装品牌、家居用品品牌等"
          }
        ],
        selectionCriteria: [
          {
            criterion: "发票获取能力",
            description: "能否规范取得增值税专用发票",
            importance: "★★★★★",
            evaluation: "这是享受退税政策的前提条件"
          },
          {
            criterion: "业务规模",
            description: "年出口额和单票货值水平",
            importance: "★★★★☆", 
            evaluation: "规模越大，相对成本越低"
          },
          {
            criterion: "合规能力",
            description: "财务和管理规范化程度",
            importance: "★★★★☆",
            evaluation: "合规成本与企业管理水平相关"
          },
          {
            criterion: "资金实力",
            description: "承受退税周期的资金能力",
            importance: "★★★☆☆",
            evaluation: "可通过融资等方式缓解"
          }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Pacifico, serif" }}>天健商业广场外贸培训学院</h1>
                <p className="text-sm text-gray-600">专业外贸知识培训平台</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                返回首页
              </button>
              <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                立即报名
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/3dd7c01ad9cded29e44b36e5b8bd1767.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-blue-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-3 mb-4">
              <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium">模块二</span>
              <span className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium">1.5小时</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4 leading-tight">
              一般贸易深度解析
            </h1>
            <p className="text-xl text-green-100 mb-6">
              传统主力 · 让学员掌握一般贸易的核心特征和操作要点，特别是出口退税的流程和意义
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-3">培训目标：</h3>
              <p className="text-green-100 leading-relaxed">
                通过本模块学习，学员将深入理解一般贸易的核心优势，掌握完整的业务流程，
                特别是出口退税的申报要点，并能根据企业特点判断是否适合采用一般贸易方式。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">详细内容</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            四大核心主题，全面掌握一般贸易的操作精髓
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={section.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div 
                className="bg-gradient-to-r from-green-600 to-green-700 p-6 cursor-pointer"
                onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className={`${section.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                          主题{index + 1}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-1">{section.title}</h3>
                      <p className="text-white/80 text-lg">{section.subtitle}</p>
                    </div>
                  </div>
                  <div className="text-white">
                    <i className={`${activeSection === section.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl`}></i>
                  </div>
                </div>
              </div>
              
              {activeSection === section.id && (
                <div className="p-8">
                  {/* Section 1: 什么是一般贸易？ */}
                  {section.id === 1 && (
                    <div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 text-white">
                          <h4 className="text-2xl font-bold mb-4">
                            <i className="ri-book-open-line mr-3"></i>
                            法律定义
                          </h4>
                          <p className="text-lg leading-relaxed mb-4">{section.content.definition}</p>
                          <div className="bg-white/10 rounded-lg p-4">
                            <h5 className="font-semibold mb-2">核心本质</h5>
                            <p className="text-green-100">{section.content.essence}</p>
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded-2xl p-8">
                          <h4 className="text-2xl font-bold text-gray-900 mb-6">
                            <i className="ri-focus-line mr-3"></i>
                            关键要素
                          </h4>
                          <div className="space-y-4">
                            {section.content.details.map((detail, idx) => (
                              <div key={idx} className="border-l-4 border-green-500 pl-4">
                                <h5 className="font-semibold text-gray-900 mb-2">{detail.title}</h5>
                                <p className="text-gray-700 text-sm leading-relaxed">{detail.content}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-6">
                        <h5 className="font-semibold text-blue-900 mb-3">
                          <i className="ri-lightbulb-line mr-2"></i>
                          关键理解
                        </h5>
                        <p className="text-blue-800">
                          一般贸易是我国外贸的主要方式，其"单笔申报、逐票核销、依法退税"的特点，
                          既保证了海关监管的有效性，也为企业享受出口退税政策提供了法律依据。
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Section 2: 核心优势与劣势 */}
                  {section.id === 2 && (
                    <div>
                      <div className="mb-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-thumb-up-line mr-3 text-green-600"></i>
                          核心优势
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {section.content.advantages.map((advantage, idx) => (
                            <div key={idx} className="bg-green-50 rounded-xl p-6 border border-green-200">
                              <h5 className="text-xl font-bold text-green-900 mb-3">{advantage.title}</h5>
                              <p className="text-green-800 mb-4">{advantage.description}</p>
                              
                              <div className="space-y-3">
                                <div className="bg-white rounded-lg p-3">
                                  <h6 className="font-semibold text-gray-900 text-sm mb-1">实例说明</h6>
                                  <p className="text-gray-700 text-sm">{advantage.example}</p>
                                </div>
                                
                                <div className="bg-white rounded-lg p-3">
                                  <h6 className="font-semibold text-gray-900 text-sm mb-1">实际影响</h6>
                                  <p className="text-gray-700 text-sm">{advantage.impact}</p>
                                </div>
                                
                                <div className="bg-green-100 rounded-lg p-3">
                                  <h6 className="font-semibold text-green-900 text-sm mb-1">数据参考</h6>
                                  <p className="text-green-800 text-sm font-medium">{advantage.calculation}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-alert-line mr-3 text-red-600"></i>
                          核心劣势
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {section.content.disadvantages.map((disadvantage, idx) => (
                            <div key={idx} className="bg-red-50 rounded-xl p-6 border border-red-200">
                              <h5 className="text-xl font-bold text-red-900 mb-3">{disadvantage.title}</h5>
                              <p className="text-red-800 mb-4">{disadvantage.description}</p>
                              
                              <div className="space-y-3">
                                <div className="bg-white rounded-lg p-3">
                                  <h6 className="font-semibold text-gray-900 text-sm mb-1">主要挑战</h6>
                                  <p className="text-gray-700 text-sm">{disadvantage.challenge}</p>
                                </div>
                                
                                <div className="bg-red-100 rounded-lg p-3">
                                  <h6 className="font-semibold text-red-900 text-sm mb-1">成本影响</h6>
                                  <p className="text-red-800 text-sm font-medium">{disadvantage.cost}</p>
                                </div>
                                
                                <div className="bg-blue-100 rounded-lg p-3">
                                  <h6 className="font-semibold text-blue-900 text-sm mb-1">应对方案</h6>
                                  <p className="text-blue-800 text-sm">{disadvantage.solution}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 3: 业务流程与关键节点 */}
                  {section.id === 3 && (
                    <div>
                      <div className="mb-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-flow-chart mr-3 text-blue-600"></i>
                          完整业务流程
                        </h4>
                        <div className="space-y-4">
                          {section.content.flowSteps.map((step, idx) => (
                            <div key={idx} className="relative">
                              <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                                <div className="flex items-start space-x-4">
                                  <div className="w-12 h-12 bg-blue-500 text-white rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0">
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-3">
                                      <h5 className="text-xl font-bold text-gray-900">{step.step}</h5>
                                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                                        <span className="bg-gray-100 px-3 py-1 rounded-full">{step.department}</span>
                                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">{step.duration}</span>
                                      </div>
                                    </div>
                                    <p className="text-gray-700 mb-4">{step.description}</p>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <h6 className="font-semibold text-gray-900 mb-2">关键要点</h6>
                                        <ul className="space-y-1">
                                          {step.keyPoints.map((point, pointIdx) => (
                                            <li key={pointIdx} className="flex items-center text-sm text-gray-700">
                                              <i className="ri-check-line mr-2 text-green-500"></i>
                                              {point}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div>
                                        <h6 className="font-semibold text-gray-900 mb-2">相关单据</h6>
                                        <ul className="space-y-1">
                                          {step.documents.map((doc, docIdx) => (
                                            <li key={docIdx} className="flex items-center text-sm text-gray-700">
                                              <i className="ri-file-text-line mr-2 text-blue-500"></i>
                                              {doc}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {idx < section.content.flowSteps.length - 1 && (
                                <div className="flex justify-center my-2">
                                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                    <i className="ri-arrow-down-line text-gray-600"></i>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white">
                          <h4 className="text-2xl font-bold mb-6">
                            <i className="ri-refund-line mr-3"></i>
                            出口退税详解
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <h5 className="font-semibold mb-2">退税条件</h5>
                              <ul className="space-y-2">
                                {section.content.taxRefundDetails.conditions.map((condition, idx) => (
                                  <li key={idx} className="flex items-start text-sm">
                                    <i className="ri-checkbox-circle-line mr-2 mt-0.5 flex-shrink-0"></i>
                                    {condition}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div className="bg-white/10 rounded-lg p-4">
                              <h5 className="font-semibold mb-2">退税周期</h5>
                              <p className="text-orange-100 text-sm">{section.content.taxRefundDetails.timeline}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded-2xl p-8">
                          <h4 className="text-2xl font-bold text-gray-900 mb-6">
                            <i className="ri-percent-line mr-3"></i>
                            常见退税率
                          </h4>
                          <div className="space-y-4">
                            {section.content.taxRefundDetails.rates.map((rate, idx) => (
                              <div key={idx} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                                <span className="font-medium text-gray-900">{rate.category}</span>
                                <span className="text-2xl font-bold text-orange-600">{rate.rate}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="bg-purple-50 rounded-2xl p-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-group-line mr-3"></i>
                          角色分工与职责
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {section.content.roleResponsibilities.map((role, idx) => (
                            <div key={idx} className="bg-white rounded-xl p-6 border border-purple-200">
                              <h5 className="text-lg font-bold text-purple-900 mb-4">{role.role}</h5>
                              <ul className="space-y-2">
                                {role.responsibilities.map((resp, respIdx) => (
                                  <li key={respIdx} className="flex items-start text-sm text-gray-700">
                                    <i className="ri-user-star-line mr-2 mt-0.5 text-purple-500 flex-shrink-0"></i>
                                    {resp}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 4: 适用场景分析 */}
                  {section.id === 4 && (
                    <div>
                      <div className="mb-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-building-line mr-3 text-green-600"></i>
                          适用企业类型
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {section.content.scenarios.map((scenario, idx) => (
                            <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                              <h5 className="text-xl font-bold text-gray-900 mb-4">{scenario.type}</h5>
                              <p className="text-gray-700 mb-4">{scenario.description}</p>
                              
                              <div className="space-y-4">
                                <div>
                                  <h6 className="font-semibold text-gray-900 mb-2">企业特征</h6>
                                  <ul className="space-y-1">
                                    {scenario.characteristics.map((char, charIdx) => (
                                      <li key={charIdx} className="flex items-start text-sm text-gray-700">
                                        <i className="ri-record-circle-line mr-2 mt-1 text-blue-500 flex-shrink-0"></i>
                                        {char}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                
                                <div>
                                  <h6 className="font-semibold text-gray-900 mb-2">主要优势</h6>
                                  <ul className="space-y-1">
                                    {scenario.advantages.map((adv, advIdx) => (
                                      <li key={advIdx} className="flex items-start text-sm text-gray-700">
                                        <i className="ri-star-line mr-2 mt-1 text-green-500 flex-shrink-0"></i>
                                        {adv}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <h6 className="font-semibold text-gray-900 mb-1 text-sm">典型案例</h6>
                                  <p className="text-gray-700 text-sm">{scenario.examples}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-yellow-50 rounded-2xl p-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-clipboard-line mr-3"></i>
                          选择标准评估
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {section.content.selectionCriteria.map((criterion, idx) => (
                            <div key={idx} className="bg-white rounded-xl p-6 border border-yellow-200">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="text-lg font-bold text-gray-900">{criterion.criterion}</h5>
                                <div className="text-yellow-500">
                                  {criterion.importance}
                                </div>
                              </div>
                              <p className="text-gray-700 mb-3">{criterion.description}</p>
                              <div className="bg-yellow-100 rounded-lg p-3">
                                <p className="text-yellow-800 text-sm font-medium">{criterion.evaluation}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold text-white mb-4">
            掌握一般贸易核心技能
          </h2>
          <p className="text-xl text-green-100 mb-8 leading-relaxed">
            深入理解出口退税政策，提升企业盈利能力，成为外贸业务专家
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 bg-white text-green-600 rounded-lg hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap cursor-pointer">
              <i className="ri-user-add-line mr-2"></i>
              立即报名学习
            </button>
            <button className="px-8 py-4 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors backdrop-blur-sm font-semibold whitespace-nowrap cursor-pointer">
              <i className="ri-download-line mr-2"></i>
              下载退税指南
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <i className="ri-graduation-cap-line text-white"></i>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "Pacifico, serif" }}>天健商业广场外贸培训学院</h3>
              </div>
              <p className="text-gray-400 mb-4">专业的外贸知识培训平台，助您成为外贸专家</p>
              <div className="flex space-x-4">
                <i className="ri-wechat-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
                <i className="ri-qq-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
                <i className="ri-mail-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">课程内容</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">外贸基础入门</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">一般贸易解析</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">市场采购贸易</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">风险防控</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">学习支持</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">在线答疑</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">实战案例</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">学习资料</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">证书认证</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">快速链接</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">关于我们</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">联系方式</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">服务条款</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 天健商业广场外贸培训学院. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}